import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOngoingOrdersApi, downloadInvoice, exportOrder } from "../../api/orders";
import { requestPickupApi } from "../../api/requestpickup";
import { generateLabelApi } from "../../api/createlabel";
import { getOrderByIdApi } from "../../api/viewOrder";
import Pagination from "../../components/Pagination/Pagination";
import { FaDownload, FaTag } from "react-icons/fa";
import DateFormat from "../../utils/DateFormat";
import { getCategories } from "../../api/categoryService";
import { FaCalendarAlt } from "react-icons/fa";

export default function OrdersList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [invoiceUrl, setInvoiceUrl] = useState("");
  const [pickupDates, setPickupDates] = useState({});
  const itemsPerPage = 10;
  const currentOrders = orders;
  /* =========================
     SAFE ERROR HANDLER
  ========================= */
  const getErrorMessage = (err, fallback) => {
    return err?.response || err?.message || fallback;
  };

  /* =========================
     FETCH ORDERS
  ========================= */
  const fetchOrders = async () => {
    setLoading(true);

    try {
      const res = await getOngoingOrdersApi(currentPage, itemsPerPage, selectedCategory);

      const data = res?.data || [];

      const updatedOrders = await Promise.all(
        data.map(async (order) => ({
          ...order,
          items: await Promise.all(
            (order.items || []).map(async (item) => ({
              ...item,
            })),
          ),
        })),
      );

      setOrders(updatedOrders);
      setTotalOrders(
        res?.pagination?.totalOrders ||
          res?.pagination?.totalItems ||
          res?.pagination?.totalitems ||
          res?.pagination?.total ||
          0,
      );
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };
  const fetchCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /* =========================
     PICKUP REQUEST
  ========================= */
  const handlePickup = async (order) => {
    try {
      const pickupDate = pickupDates[order._id];

      if (!pickupDate) {
        alert("Please select pickup date");
        return;
      }

      const res = await requestPickupApi(order._id, pickupDate);

      if (!res || res?.success === false) {
        throw new Error(res?.message || "Pickup request failed");
      }

      alert(res?.message || "Pickup Requested Successfully");
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  /* =========================
     GENERATE LABEL
  ========================= */
  const handleGenerateLabel = async (order) => {
    try {
      const res = await generateLabelApi(order._id);

      let labelUrl = res?.label_url;

      if (!labelUrl) {
        const orderRes = await getOrderByIdApi(order._id);
        labelUrl = orderRes?.shipment?.label_url;
      }

      if (labelUrl) {
        // Update label URL in orders
        setOrders((prev) =>
          prev.map((o) =>
            o._id === order._id
              ? {
                  ...o,
                  shipment: {
                    ...o.shipment,
                    label_url: labelUrl,
                  },
                }
              : o,
          ),
        );

        // Navigate to View Order
        navigate("/orders/view", {
          state: {
            orderId: order._id,
          },
        });
      } else {
        alert("Label not available");
      }
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      const response = await downloadInvoice(orderId);

      if (!response?.success || !response?.data) {
        throw new Error("Invoice URL not found");
      }

      const pdfUrl = response.data;

      // Fetch PDF as blob
      const pdfResponse = await fetch(pdfUrl);

      if (!pdfResponse.ok) {
        throw new Error("Failed to download invoice");
      }

      const blob = await pdfResponse.blob();

      // Create temporary download URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `invoice-${orderId}.pdf`;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  const handleExportOrder = async () => {
    try {
      const response = await exportOrder();

      const blob = new Blob([response.data], {
        type: "text/csv",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      // Get filename from response header if available
      const contentDisposition = response.headers["content-disposition"];

      let fileName = "orders.csv";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  return (
    <div className="container-fluid p-4 np-orders-page">
      <div className="d-flex align-items-center justify-content-end mb-3">
        <select
          className="form-select me-2"
          style={{ width: "220px" }}
          value={selectedCategory}
          onChange={handleCategoryChange}
        >
          <option value="">All Categories</option>

          {categories.map((category) => (
            <option
              key={category._id}
              value={category.slug || category.name?.trim().toLowerCase().replace(/\s+/g, "-")}
            >
              {category.name}
            </option>
          ))}
        </select>

        <button className="btn btn-success" onClick={handleExportOrder}>
          Export CSV
        </button>
      </div>
      {loading && <p>Loading orders...</p>}

      {!loading && orders.length === 0 && <p className="text-gray-500">No orders found</p>}

      <div className="row">
        {currentOrders.map((order) => (
          <div key={order._id} className="col-md-6 mb-3">
            <div
              className="border rounded p-3 shadow card"
              style={{ cursor: "pointer" }}
              onClick={() =>
                navigate("/orders/view", {
                  state: { orderId: order._id },
                })
              }
            >
              {/* HEADER */}
              <div className="row w-100 align-items-center">
                {/* COLUMN 1 - Order Data */}
                <div className="col-md-7">
                  <p className="small mb-0">{order.orderId}</p>

                  <p className="fw-semibold mb-0">
                    <DateFormat date={order.createdAt} />
                  </p>
                </div>

                {/* COLUMN 2 - Buttons */}
                <div className="col-md-5">
                  {order.status !== "CANCELLED" && order.status !== "CANCELED" && (
                    <div className="d-flex justify-content-end align-items-center gap-1 flex-wrap">
                      {!order.shipment?.label_url ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-dark text-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateLabel(order);
                          }}
                        >
                          <FaTag className="me-1" />
                          Generate Label
                        </button>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={(e) => {
                              e.stopPropagation();

                              const input = document.getElementById(`pickup-date-${order._id}`);

                              if (input) {
                                input.showPicker();
                              }
                            }}
                          >
                            <FaCalendarAlt />
                          </button>

                          <input
                            type="date"
                            id={`pickup-date-${order._id}`}
                            aria-label={`Pickup date for order ${order.orderId}`}
                            style={{
                              position: "absolute",
                              width: "38px",
                              height: "38px",
                              opacity: 0,
                              pointerEvents: "none",
                              left: 0,
                            }}
                            min={new Date().toISOString().split("T")[0]}
                            value={pickupDates[order._id] || ""}
                            onChange={(e) => {
                              e.stopPropagation();

                              setPickupDates((prev) => ({
                                ...prev,
                                [order._id]: e.target.value,
                              }));
                            }}
                          />

                          <button
                            type="button"
                            className="btn btn-sm btn-secondary text-nowrap"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePickup(order);
                            }}
                          >
                            Pickup Request
                          </button>
                        </div>
                      )}

                      {/* Download Invoice */}
                      {order?.invoice?.invoiceKey && (
                        <span className="invoice-download-tooltip">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            aria-label="Download Invoice"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadInvoice(order.orderId);
                            }}
                          >
                            <FaDownload />
                          </button>
                          <span className="invoice-download-tooltip-text" role="tooltip">
                            Download Invoice
                          </span>
                        </span>
                      )}

                      {/* Verify Order */}
                      {order.tracking?.[order.tracking.length - 1]?.status === "PLACED" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-success text-nowrap"
                          onClick={(e) => {
                            e.stopPropagation();

                            navigate("/orders/verify", {
                              state: {
                                orderId: order._id,
                                orderNumber: order.orderId,
                              },
                            });
                          }}
                        >
                          Verify Order
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* CUSTOMER */}
              <div className="mt-2 mb-2">
                <p className="mb-0">
                  <strong>{order.address?.fullName || "N/A"}</strong> (
                  {order.address?.phone || "N/A"})
                </p>
                <p className="mb-0">
                  {order.address?.address}, {order.address?.city}, {order.address?.state},{" "}
                  {order.address?.pincode}
                </p>
              </div>

              {/* ITEMS */}
              <div className="row border-top">
                <div className="col-md-6 pt-2 mt-2">
                  {order.items?.map((item, i) => (
                    <div key={i} className="d-flex justify-content-between">
                      <div>
                        <span>
                          {item.name} × {item.quantity}
                        </span>
                        <p className="text-sm mb-0">{item.customText}</p>
                        <p className="mb-0 small">
                          <span className="fw-bold">Variation:</span>
                          {item.selections?.variantName || "N/A"}
                        </p>
                        <p className="mb-0 small">Font: {item.selections?.fontId?.name}</p>

                        <p className="mb-0 small">Material: {item.selections?.materialId?.value}</p>

                        <p className="mb-0 small">Size: {item.selections?.sizeId?.value}</p>

                        <p className="mb-0 small">Color: {item.selections?.colorId?.name}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-md-6 mt-3">
                  <div className="d-flex justify-content-end">
                    <div className="text-end w-100">
                      <p className="">
                        <span>Price: </span>₹{order.subtotal}
                      </p>
                      <p className="mb-0">
                        <span>Shipping: </span>₹{order.shipment.shippingCharge}
                      </p>
                      <p className="mb-0">
                        <span>Tax: </span>₹{order.tax.totalTax}
                      </p>

                      <p className="border-top pt-2 mb-2">
                        <strong>Total: </strong>

                        {order.discountedTotal ? (
                          <>
                            <strong>₹{Number(order.discountedTotal).toFixed(2)}</strong>
                          </>
                        ) : (
                          <strong>₹{order.total}</strong>
                        )}
                      </p>

                      <span
                        className={`px-2 py-1 rounded ${
                          order.status === "PAID"
                            ? "text-success"
                            : order.status === "REFUND_INITIATED"
                              ? "text-danger"
                              : "text-warning"
                        }`}
                      >
                        {order.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* PAGINATION */}
        <Pagination
          totalItems={totalOrders}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
